// ─────────────────────────────────────────────────────────────────────────────
// SATIŞ TERMİNALİ CİHAZLARI (11 haneli kod)
//
// "Login İşlemleri" sekmesinin alt bölümü. Cihaz yönetimindeki "Satış Ekranı
// Ekle" öğesiyle aynı kaydı üretir (DeviceScreens, ScreenType = salesTerminal):
// iki yoldan üretilen kodlar aynı listede görünür.
//
// Kod backend'de üretilir; burada kod üretme mantığı yoktur.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { mainApi } from '../../../../config/apiClient';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import { TerminalDeviceMode } from '../../../../services/auth/terminalSession';

const SALES_TERMINAL_SCREEN_TYPE = 'salesTerminal';

interface SalesTerminalDevice {
  id: number;
  screenCode: string;
  screenName: string;
  status?: string;
  lastSeen?: string;
  createdAt?: string;
}

interface SalesTerminalDevicesSectionProps {
  companyId: string | null;
  /**
   * Kodun bağlanacağı ekranın kiracı DB'sindeki Id'si. Restoran modunda
   * RestaurantScreens.Id, paket modunda PackageScreens.Id, hızlı satışta
   * QuickSaleScreens.Id.
   */
  linkedScreenId: number | null;
  deviceMode: TerminalDeviceMode;
  screenName?: string;
}

const SalesTerminalDevicesSection: React.FC<SalesTerminalDevicesSectionProps> = ({
  companyId,
  linkedScreenId,
  deviceMode,
  screenName,
}) => {
  const { t } = useTranslation('salesSystem');
  const isMobile = useMediaQuery('(max-width: 1499px)');

  const [devices, setDevices] = useState<SalesTerminalDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [adding, setAdding] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState<SalesTerminalDevice | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const canInteract = !!companyId && !!linkedScreenId;

  /**
   * Cihaz listesi endpoint'i ekran tipine göre süzme yapmıyor, bu yüzden
   * şirketin tüm cihazları çekilip bu ekrana bağlı satış terminalleri
   * ayıklanıyor. Şube parametresi gönderilmiyor: terminalin şubesi bağlı
   * ekrandan devralınıyor ve linkedScreenId eşleşmesi zaten tek bir ekranı
   * işaret ediyor.
   */
  const loadDevices = useCallback(async () => {
    if (!companyId || !linkedScreenId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await mainApi.get('/api/device/list', { params: { companyId } });
      const all: any[] = response.data?.screens || [];
      const mine = all
        .filter(
          (d) =>
            d.screenType === SALES_TERMINAL_SCREEN_TYPE &&
            d.deviceMode === deviceMode &&
            d.linkedScreenId === linkedScreenId,
        )
        .map((d) => ({
          id: d.id,
          screenCode: d.screenCode,
          screenName: d.screenName,
          status: d.status,
          lastSeen: d.lastSeen,
          createdAt: d.createdAt,
        }));
      setDevices(mine);
    } catch (err: any) {
      console.error('Satış terminali listesi yükleme hatası:', err);
      setError(err?.response?.data?.message || t('salesTerminalDevices.errors.listLoadFailed'));
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, linkedScreenId, deviceMode, t]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleAddDevice = async () => {
    if (!companyId || !linkedScreenId) return;

    const trimmed = newDeviceName.trim();
    if (!trimmed) {
      setError(t('salesTerminalDevices.errors.deviceNameRequired'));
      return;
    }

    setAdding(true);
    setError(null);
    try {
      const response = await mainApi.post('/api/device/add', {
        companyId,
        screenType: SALES_TERMINAL_SCREEN_TYPE,
        deviceMode,
        linkedScreenId,
        screenName: trimmed,
        createdBy: ownAuthSession.getCurrentUid(),
      });

      if (!response.data?.success) {
        setError(response.data?.message || t('salesTerminalDevices.errors.addFailed'));
        return;
      }

      setAddDialogOpen(false);
      setNewDeviceName('');
      await loadDevices();
    } catch (err: any) {
      console.error('Satış terminali ekleme hatası:', err);
      setError(err?.response?.data?.message || t('salesTerminalDevices.errors.addFailed'));
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteDevice = async () => {
    if (!deletingDevice || !companyId) return;

    setDeleting(true);
    try {
      await mainApi.post('/api/device/delete', {
        screenId: deletingDevice.id,
        companyId,
        deletedBy: ownAuthSession.getCurrentUid(),
      });
      setDeleteDialogOpen(false);
      setDeletingDevice(null);
      await loadDevices();
    } catch (err: any) {
      console.error('Satış terminali silme hatası:', err);
      setError(err?.response?.data?.message || t('salesTerminalDevices.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusChip = (status?: string) => {
    switch (status) {
      case 'active':
        return <Chip icon={<CheckCircleIcon />} label={t('salesTerminalDevices.status.active')} color="success" size="small" />;
      case 'pending':
        return <Chip icon={<PendingIcon />} label={t('salesTerminalDevices.status.pendingSetup')} color="warning" size="small" />;
      case 'inactive':
        return <Chip icon={<CancelIcon />} label={t('salesTerminalDevices.status.passive')} color="error" size="small" />;
      default:
        return <Chip label={status || '-'} size="small" />;
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const openAddDialog = () => {
    const base = screenName || t('salesTerminalDevices.defaultNameFallback');
    setNewDeviceName(`${base} ${devices.length + 1}`);
    setAddDialogOpen(true);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <StorefrontIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">
            {t('salesTerminalDevices.title')}
          </Typography>
          <Chip label={t('salesTerminalDevices.deviceCount', { count: devices.length })} size="small" variant="outlined" />
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('salesTerminalDevices.actions.refresh')}>
            <span>
              <IconButton onClick={loadDevices} size="small" disabled={!canInteract || loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={openAddDialog}
            disabled={!canInteract}
          >
            {t('salesTerminalDevices.actions.generateCode')}
          </Button>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
        {t('salesTerminalDevices.description')}
      </Typography>

      {!canInteract && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('salesTerminalDevices.saveScreenSettingsHint')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : devices.length === 0 ? (
        canInteract && (
          <Paper
            variant="outlined"
            sx={{ p: 3, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}
          >
            <StorefrontIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('salesTerminalDevices.empty.title')}
            </Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAddDialog} sx={{ mt: 1 }}>
              {t('salesTerminalDevices.empty.addFirst')}
            </Button>
          </Paper>
        )
      ) : !isMobile ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('salesTerminalDevices.table.deviceName')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('salesTerminalDevices.table.screenCode')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('salesTerminalDevices.table.status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('salesTerminalDevices.table.lastSeen')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {t('salesTerminalDevices.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.screenCode} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {device.screenName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" sx={{ letterSpacing: 1 }}>
                        {device.screenCode}
                      </Typography>
                      <Tooltip
                        title={
                          copiedCode === device.screenCode
                            ? t('salesTerminalDevices.actions.copied')
                            : t('salesTerminalDevices.actions.copy')
                        }
                      >
                        <IconButton size="small" onClick={() => handleCopyCode(device.screenCode)}>
                          <ContentCopyIcon
                            fontSize="small"
                            color={copiedCode === device.screenCode ? 'success' : 'action'}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(device.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(device.lastSeen)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('salesTerminalDevices.actions.deleteDevice')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeletingDevice(device);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          {devices.map((device) => (
            <Card key={device.screenCode} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {device.screenName}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" sx={{ letterSpacing: 1 }}>
                        {device.screenCode}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyCode(device.screenCode)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {getStatusChip(device.status)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t('salesTerminalDevices.table.lastSeen')}: {formatDate(device.lastSeen)}
                </Typography>
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon fontSize="small" />}
                    onClick={() => {
                      setDeletingDevice(device);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    {t('salesTerminalDevices.actions.delete')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Kod Üret */}
      <Dialog open={addDialogOpen} onClose={() => !adding && setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('salesTerminalDevices.addDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('salesTerminalDevices.addDialog.description')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('salesTerminalDevices.addDialog.deviceName')}
            value={newDeviceName}
            onChange={(e) => setNewDeviceName(e.target.value)}
            sx={{ mt: 1 }}
            disabled={adding}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit" disabled={adding}>
            {t('salesTerminalDevices.common.cancel')}
          </Button>
          <Button
            onClick={handleAddDevice}
            variant="contained"
            disabled={adding || !newDeviceName.trim()}
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {adding ? t('salesTerminalDevices.actions.generating') : t('salesTerminalDevices.actions.generateCode')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sil */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('salesTerminalDevices.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('salesTerminalDevices.deleteDialog.description', { name: deletingDevice?.screenName })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('salesTerminalDevices.table.screenCode')}: <strong>{deletingDevice?.screenCode}</strong>
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {t('salesTerminalDevices.deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={deleting}>
            {t('salesTerminalDevices.common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteDevice}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('salesTerminalDevices.actions.deleting') : t('salesTerminalDevices.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesTerminalDevicesSection;
